import { useTranslation } from 'react-i18next';
import type { IAdminTag } from '@interfaces/IAdminTag';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { LinkIcon, PencilIcon, UnlinkIcon } from '@/utils/icons';
import { isTagAssigned } from '../utils/tagFilters';
import { BatteryCell, CertificatesCell, LastSeenCell, LinkedToCell, StatusCell } from './cells';
import styles from '../../adminTable.module.css';
import rowStyles from './TagRow.module.css';

interface ITagRowProps {
  tag: IAdminTag;
  onAssign: (tag: IAdminTag) => void;
  onEdit: (tag: IAdminTag) => void;
  onUnassign: (tag: IAdminTag) => void;
}

export function TagRow({ tag, onAssign, onEdit, onUnassign }: ITagRowProps) {
  const { t } = useTranslation();
  const assigned = isTagAssigned(tag);

  // Editable = worker + manager + scissors-lift tags (manager edit =
  // certificates only; scissors edit = profession + nickname); hoist and
  // visitor tags have nothing to edit.
  const editable =
    assigned &&
    ((tag.kind === 'worker' && tag.profession?.tradeId !== 'guest') ||
      tag.kind === 'manager' ||
      (tag.kind === 'utility' && tag.linkedUtility?.tradeId === 'scissorsLift'));
  
    return (
    <tr className={styles.row}>
      <td className={styles.td}>{tag.tagNick ?? tag.tagId}</td>
      <td className={styles.td}><LinkedToCell tag={tag} /></td>
      <td className={styles.td}>{tag.fullName}</td>
      <td className={styles.td}>{tag.idNumber ?? <span className={styles.muted}>—</span>}</td>
      <td className={styles.td}>{tag.phoneNumber ?? <span className={styles.muted}>—</span>}</td>
      <td className={`${styles.td} ${styles.tdCenter}`}><CertificatesCell tag={tag} /></td>
      <td className={`${styles.td} ${styles.tdCenter}`}><StatusCell status={tag.status} /></td>
      <td className={`${styles.td} ${styles.tdCenter}`}><LastSeenCell lastSeen={tag.lastSeen} floorNick={tag.lastFloorNick} /></td>
      <td className={`${styles.td} ${styles.tdCenter}`}><BatteryCell battery={tag.batteryLevel} /></td>
      <td className={`${styles.td} ${styles.tdCenter}`}>
        <span className={rowStyles.actionsCell}>
          {!assigned && (
            <Tooltip content={t('adminTags.assign.action')}>
              <button
                type="button"
                className={rowStyles.actionBtn}
                aria-label={t('adminTags.assign.action')}
                onClick={() => onAssign(tag)}
              >
                <LinkIcon size={15} />
              </button>
            </Tooltip>
          )}
          {editable && (
            <Tooltip content={t('adminTags.assign.editTitle')}>
              <button
                type="button"
                className={rowStyles.actionBtn}
                aria-label={t('adminTags.assign.editTitle')}
                onClick={() => onEdit(tag)}
              >
                <PencilIcon size={15} />
              </button>
            </Tooltip>
          )}
          {assigned && (
            <Tooltip content={t('adminTags.unassignFlow.action')}>
              <button
                type="button"
                className={rowStyles.actionBtn}
                aria-label={t('adminTags.unassignFlow.action')}
                onClick={() => onUnassign(tag)}
              >
                <UnlinkIcon size={15} />
              </button>
            </Tooltip>
          )}
        </span>
      </td>
    </tr>
  );
}